import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ImagePlus, Trash2, X } from 'lucide-react';
import AuthenticatedBlobImage from '../Common/AuthenticatedBlobImage';

/**
 * Slide horizontal no padrão Figma (170×100, r12, gap 10) + lightbox.
 * items: { id, src?, path?, alt? }
 */
export default function ComplaintPhotoSlider({
  items = [],
  onRemove,
  onAdd,
  adding = false,
  addLabel = 'Adicionar foto',
  emptyLabel = 'Nenhuma evidência fotográfica.',
  title = 'Evidências Fotográficas',
  accept = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp',
  multiple = true,
}) {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (lightbox == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((i) => (i == null ? i : (i + 1) % items.length));
      if (e.key === 'ArrowLeft') {
        setLightbox((i) => (i == null ? i : (i - 1 + items.length) % items.length));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, items.length]);

  const handleFiles = (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = '';
    if (!files.length || !onAdd) return;
    onAdd(files);
  };

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[13px] font-bold text-[#141D19]">{title}</p>
        {onAdd ? (
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-semibold text-[#0F9F90]">
            <ImagePlus className="h-4 w-4" aria-hidden />
            {adding ? 'Enviando…' : addLabel}
            <input
              type="file"
              accept={accept}
              multiple={multiple}
              className="hidden"
              disabled={adding}
              onChange={handleFiles}
            />
          </label>
        ) : null}
      </div>

      {items.length === 0 && !onAdd ? (
        <p className="text-sm text-[#869990]">{emptyLabel}</p>
      ) : (
        <div className="flex gap-[10px] overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((item, idx) => (
            <button
              key={item.id || idx}
              type="button"
              onClick={() => setLightbox(idx)}
              className="group relative h-[100px] w-[170px] shrink-0 overflow-hidden rounded-xl bg-[#E2ECE5] text-left"
            >
              {item.src ? (
                <img src={item.src} alt={item.alt || `Foto ${idx + 1}`} className="h-full w-full object-cover" />
              ) : item.path ? (
                <AuthenticatedBlobImage
                  path={item.path}
                  alt={item.alt || `Foto ${idx + 1}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full animate-pulse bg-[#E2ECE5]" />
              )}
              {onRemove && item.id ? (
                <span
                  role="button"
                  tabIndex={0}
                  className="absolute right-1.5 top-1.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 opacity-0 shadow-sm transition group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onRemove(item);
                    }
                  }}
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                  <span className="sr-only">Remover</span>
                </span>
              ) : null}
            </button>
          ))}
          {onAdd ? (
            <label className="flex h-[100px] w-[170px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-[#E2ECE5] bg-white text-[#4A5D54]">
              <ImagePlus className="h-5 w-5 text-[#0F9F90]" aria-hidden />
              <span className="text-[11px] font-semibold">{adding ? 'Enviando…' : 'Nova foto'}</span>
              <input
                type="file"
                accept={accept}
                multiple={multiple}
                className="hidden"
                disabled={adding}
                onChange={handleFiles}
              />
            </label>
          ) : null}
        </div>
      )}

      {lightbox != null && items[lightbox] ? (
        <div
          className="fixed inset-0 z-[80] flex flex-col bg-[#141D19]/92"
          role="dialog"
          aria-modal="true"
          aria-label="Slide de evidências"
        >
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <p className="text-sm font-semibold">
              {lightbox + 1} / {items.length}
            </p>
            <button
              type="button"
              className="rounded-full p-2 hover:bg-white/10"
              onClick={() => setLightbox(null)}
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-12 pb-8">
            {items.length > 1 ? (
              <button
                type="button"
                className="absolute left-3 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
                onClick={() => setLightbox((i) => (i - 1 + items.length) % items.length)}
                aria-label="Foto anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : null}
            <div className="max-h-full max-w-full">
              {items[lightbox].src ? (
                <img
                  src={items[lightbox].src}
                  alt={items[lightbox].alt || `Foto ${lightbox + 1}`}
                  className="max-h-[78vh] max-w-[min(92vw,960px)] rounded-xl object-contain"
                />
              ) : items[lightbox].path ? (
                <AuthenticatedBlobImage
                  path={items[lightbox].path}
                  alt={items[lightbox].alt || `Foto ${lightbox + 1}`}
                  className="max-h-[78vh] max-w-[min(92vw,960px)] rounded-xl object-contain"
                />
              ) : null}
            </div>
            {items.length > 1 ? (
              <button
                type="button"
                className="absolute right-3 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
                onClick={() => setLightbox((i) => (i + 1) % items.length)}
                aria-label="Próxima foto"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            ) : null}
          </div>
          {items.length > 1 ? (
            <div className="flex justify-center gap-1.5 pb-5">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Ir para foto ${i + 1}`}
                  className={`h-1.5 rounded-full ${i === lightbox ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`}
                  onClick={() => setLightbox(i)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
